import { IStoreState } from 'store';
import { EParetoActionType, EParetoDealFilter, IParetoDeal, IParetoInput } from 'store/pareto/types';
import { setLoading } from 'store/loading/actions';
import { ELoadingType } from 'store/loading/types';
import apiService from 'services/api';
import { getAccessToken } from 'lib/auth';
import apiRoutes from 'services/api/routes';
import { getAxiosErrorMessage, handleError } from 'lib/helpers/error-handler';
import { EStorageField, EModalTitle } from 'lib/storage';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';

export const setParetoDeals = (paretoDeals: IParetoDeal[]) => (dispatch: ThunkDispatch<void, void, AnyAction>) => {
  localStorage.setItem(EStorageField.PARETO_DEALS, JSON.stringify(paretoDeals));
  dispatch({
    type: EParetoActionType.SET_PARETO_DEALS,
    payload: paretoDeals,
  });
};

export const setParetoDealFilter = (dealFilter: EParetoDealFilter) => ({
  type: EParetoActionType.SET_DEAL_FILTER,
  payload: dealFilter,
});

export const setParetoRegionFilter = (regionFilter: string) => ({
  type: EParetoActionType.SET_REGION_FILTER,
  payload: regionFilter,
});

export const setParetoProductLineFilter = (productLineFilter: string) => ({
  type: EParetoActionType.SET_PRODUCT_LINE_FILTER,
  payload: productLineFilter,
});

export const setParetoSearchKeyword = (searchKeyword: string) => ({
  type: EParetoActionType.SET_SEARCH_KEYWORD,
  payload: searchKeyword,
});

export const setParetoIsModalOpen = (isModalOpen: boolean, modalTitle: EModalTitle) => ({
  type: EParetoActionType.SET_IS_MODAL_OPEN,
  payload: { isModalOpen, modalTitle },
});

export const setParetoHasVolumeWidthBars = (hasVolumeWidth: boolean) => {
  localStorage.setItem(EStorageField.PARETO_HAS_VOLUME_WIDTH_BARS, JSON.stringify(hasVolumeWidth));
  return {
    type: EParetoActionType.SET_BAR_WIDTH,
    payload: hasVolumeWidth,
  };
};

export const evaluateParetoDeals = () => {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    dispatch(setLoading(ELoadingType.SPINNER));

    const { pareto, dealEvaluation } = getState();
    const { paretoDealIds } = pareto;
    const { selectedUnitType, selectedForecastType, selectedCostFormulaType } = dealEvaluation;

    const postBody: IParetoInput = {
      dealIDs: paretoDealIds,
    };

    apiService
      .post(apiRoutes.paretoEvaluation, postBody, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
        params: {
          forecast: selectedForecastType,
          costType: selectedCostFormulaType,
          outputUnit: selectedUnitType,
        },
      })
      .then(response => {
        const { errors, result } = response.data;
        if (errors.length) {
          handleError(errors[0]);
          return;
        }
        dispatch(setParetoDeals(result));
        dispatch(setParetoIsModalOpen(false, pareto.modalTitle));
      })
      .catch(error => {
        const errorMessage = getAxiosErrorMessage(error);
        handleError(errorMessage);
      })
      .finally(() => dispatch(setLoading(ELoadingType.NONE)));
  };
};

export const setParetoDealIds = (paretoDealIds: string[]) => (
  dispatch: ThunkDispatch<IStoreState, void, AnyAction>
) => {
  localStorage.setItem(EStorageField.PARETO_DEAL_IDS, JSON.stringify(paretoDealIds));
  dispatch({
    type: EParetoActionType.SET_PARETO_DEAL_IDS,
    payload: paretoDealIds,
  });
  dispatch(evaluateParetoDeals());
};

export const removeParetoDealAndId = (paretoDealName: string, paretoDealId: string) => {
  return (dispatch: ThunkDispatch<IStoreState, void, AnyAction>, getState: () => IStoreState) => {
    const { pareto } = getState();
    const { paretoDeals, paretoDealIds } = pareto;
    const paretoDealsUpdated: IParetoDeal[] = paretoDeals.filter(paretoDeal => paretoDeal.name !== paretoDealName);
    const paretoDealIdsUpdated: string[] = paretoDealIds.filter(pid => pid !== paretoDealId);

    localStorage.setItem(EStorageField.PARETO_DEALS, JSON.stringify(paretoDealsUpdated));
    localStorage.setItem(EStorageField.PARETO_DEAL_IDS, JSON.stringify(paretoDealIdsUpdated));

    dispatch({
      type: EParetoActionType.SET_PARETO_DEALS,
      payload: paretoDealsUpdated,
    });
    dispatch({
      type: EParetoActionType.SET_PARETO_DEAL_IDS,
      payload: paretoDealIdsUpdated,
    });
  };
};
